export enum AsyncState {
  Initial,
  Pending,
  Done,
  Error,
}

export enum HTTPStatus {
  Unauthorized = "UNAUTHORIZED",
  BadRequest = "BAD_REQUEST",
  Forbidden = "FORBIDDEN",
  NotFound = "NOT_FOUND",
  Conflict = "CONFLICT",
  UnprocessableEntity = "UNPROCESSABLE_ENTITY",
  TooManyRequests = "TOO_MANY_REQUESTS",
  Unknown = "UNKNOWN",
}

export interface ServiceErrorResponse {
  readonly code?: string;
  readonly details?: string;
  readonly message?: string;
  readonly requestId?: string;
  readonly validationErrors?: {
    readonly [key: string]: unknown;
  };
}

const responseStatusToMessage = (status: number): HTTPStatus => {
  switch (status) {
    case 400:
      return HTTPStatus.BadRequest;
    case 401:
      return HTTPStatus.Unauthorized;
    case 403:
      return HTTPStatus.Forbidden;
    case 404:
      return HTTPStatus.NotFound;
    case 409:
      return HTTPStatus.Conflict;
    case 422:
      return HTTPStatus.UnprocessableEntity;
    case 429:
      return HTTPStatus.TooManyRequests;
    default:
      return HTTPStatus.Unknown;
  }
};

const buildServiceError = async <T extends ServiceErrorResponse>(
  response: Response
): Promise<ServiceError<T>> => {
  const jsonResponse = await response.json().catch(() => ({}));
  return new ServiceError(responseStatusToMessage(response.status), {
    code: jsonResponse.code,
    details: jsonResponse.details,
    message: jsonResponse.message,
    requestId: jsonResponse.requestId,
    validationErrors: jsonResponse.validationErrors,
  } as T);
};

export class ServiceError<T extends ServiceErrorResponse = ServiceErrorResponse> extends Error {
  readonly serviceErrorResponse: T;

  constructor(message: string, serviceErrorResponse: T) {
    super(message);
    this.name = "ServiceError";
    this.serviceErrorResponse = serviceErrorResponse;
  }

  toString(): string {
    return `${this.message} - ${JSON.stringify(this.serviceErrorResponse)}`;
  }

  hasCode(code: string): boolean {
    return this.serviceErrorResponse.code === code;
  }

  hasStatusAndCode(status: HTTPStatus, code: string): boolean {
    return this.message === status && this.hasCode(code);
  }

  static async fromFetchResponse(response: Response): Promise<ServiceError> {
    return buildServiceError(response);
  }

  public assertType<ToAssert extends T>(): asserts this is ServiceError<ToAssert> {
    // This is a no-op, but it allows us to use this method in a type guard.
  }
}
